import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Users, GamepadIcon, DollarSign, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CreateGroupDialog } from "./CreateGroupDialog";
import { GroupCard } from "./GroupCard";
import { UserProfileDropdown } from "./UserProfileDropdown";
import { Footer } from "./Footer";
import { usePWAStandalone } from "@/hooks/usePWAStandalone";
import { User } from "@supabase/supabase-js";

interface Group {
  id: string;
  name: string;
  created_at: string;
  playerCount?: number;
  activeGamesCount?: number;
}

interface DashboardProps {
  onLogout: () => void;
  user: User;
}

export const Dashboard = ({ onLogout, user }: DashboardProps) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [totalBuyins, setTotalBuyins] = useState(0);
  const [netAmount, setNetAmount] = useState(0);
  const [totalGames, setTotalGames] = useState(0);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [sessionReady, setSessionReady] = useState(false);
  const { toast } = useToast();
  const isStandalone = usePWAStandalone();

  useEffect(() => {
    console.log('=== Dashboard useEffect triggered ===');
    console.log('User prop:', user);
    
    const initializeDashboard = async () => {
      try {
        // Ensure we have a valid session before proceeding
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('Session check:', { session: session?.user?.id, error: sessionError });
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          toast({
            title: "Authentication Error",
            description: "Please refresh the page and try again.",
            variant: "destructive"
          });
          return;
        }
        
        if (!session?.user) {
          console.error('No active session found');
          toast({
            title: "Session Error",
            description: "Your session has expired. Please log in again.",
            variant: "destructive"
          });
          return;
        }
        
        console.log('Session is valid, proceeding with data fetch');
        setSessionReady(true);
        await fetchGroups();
      } catch (error) {
        console.error('Dashboard initialization error:', error);
        toast({
          title: "Initialization Error",
          description: "Failed to initialize dashboard. Please refresh the page.",
          variant: "destructive"
        });
      }
    };

    if (user?.id) {
      initializeDashboard();
    } else {
      console.warn('No user provided to Dashboard component');
    }
  }, [user]);

  const fetchGroups = async () => {
    try {
      console.log('=== fetchGroups called ===');
      setLoading(true);
      
      // Get current user with extensive logging
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      console.log('Current user from auth:', { 
        user: currentUser?.id, 
        email: currentUser?.email, 
        error: userError 
      });
      
      if (userError || !currentUser) {
        console.error('User error in fetchGroups:', userError);
        toast({
          title: "Authentication Error",
          description: "Unable to verify your identity. Please log in again.",
          variant: "destructive"
        });
        return;
      }

      // First, fix any historical data issues for this user
      console.log('Calling fix_user_historical_data...');
      const { error: fixError } = await supabase.rpc('fix_user_historical_data', { 
        p_user_id: currentUser.id 
      });
      if (fixError) {
        console.error('Error fixing historical data:', fixError);
      } else {
        console.log('Historical data fix completed');
      }

      // Get accessible groups using RPC function with debugging
      console.log('Calling get_user_accessible_groups...');
      const { data: accessibleGroupIds, error: accessError } = await supabase
        .rpc('get_user_accessible_groups', { _user_id: currentUser.id });
      
      console.log('Accessible group IDs:', { data: accessibleGroupIds, error: accessError });
      
      if (accessError) {
        console.error('Error getting accessible groups:', accessError);
        toast({
          title: "Data Access Error",
          description: "Unable to retrieve your groups. Please try refreshing the page.",
          variant: "destructive"
        });
        return;
      }

      if (!accessibleGroupIds || accessibleGroupIds.length === 0) {
        console.log('No accessible groups found for user');
        setGroups([]);
        await calculateUserStats();
        return;
      }

      // Extract group IDs from the response
      const groupIds = accessibleGroupIds.map((item: any) => 
        typeof item === 'string' ? item : item.group_id
      );
      console.log('Extracted group IDs:', groupIds);

      // Fetch groups with enhanced data
      console.log('Fetching group details...');
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          created_at,
          created_by
        `)
        .in('id', groupIds);

      console.log('Groups data:', { data: groupsData, error: groupsError });

      if (groupsError) {
        console.error('Error fetching groups:', groupsError);
        toast({
          title: "Database Error",
          description: "Unable to load group details. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Fetch stats for each group with error handling
      console.log('Fetching group statistics...');
      const groupsWithStats = await Promise.all((groupsData || []).map(async (group) => {
        try {
          // Get player count
          const { count: playerCount, error: playerError } = await supabase
            .from('players')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id);

          if (playerError) {
            console.error(`Error fetching player count for group ${group.id}:`, playerError);
          }

          // Get active games count
          const { count: activeGamesCount, error: gamesError } = await supabase
            .from('games')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id)
            .eq('status', 'in_progress');

          if (gamesError) {
            console.error(`Error fetching games count for group ${group.id}:`, gamesError);
          }

          return {
            ...group,
            playerCount: playerCount || 0,
            activeGamesCount: activeGamesCount || 0
          };
        } catch (error) {
          console.error(`Error processing group ${group.id}:`, error);
          return {
            ...group,
            playerCount: 0,
            activeGamesCount: 0
          };
        }
      }));

      console.log('Final groups with stats:', groupsWithStats);
      setGroups(groupsWithStats);
      
      // Calculate user stats
      console.log('Calculating user stats...');
      await calculateUserStats();
      
      toast({
        title: "Groups Loaded",
        description: `Found ${groupsWithStats.length} accessible groups.`,
      });
      
    } catch (error) {
      console.error('Error in fetchGroups:', error);
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred while loading your groups.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateUserStats = async () => {
    try {
      console.log('=== Starting calculateUserStats ===');
      console.log('Current user:', user.id, user.email);
      
      // Get user's profile to find linked player data
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', user.id)
        .single();

      const userEmail = profile?.email || user.email;
      console.log('User email for matching:', userEmail);

      if (!userEmail) {
        console.log('No user email found, returning');
        return;
      }

      // Find all players with this email across all groups
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id, group_id, name, email')
        .eq('email', userEmail);

      console.log('Players found:', players, 'Error:', playersError);

      if (!players || players.length === 0) {
        console.log('No players found for this email');
        return;
      }

      const playerIds = players.map(p => p.id);
      console.log('Player IDs:', playerIds);

      // Get all games where user has buyins (for total games count)
      const { data: allBuyins, error: allBuyinsError } = await supabase
        .from('buyins')
        .select('amount, player_id, game_id')
        .in('player_id', playerIds);

      console.log('All buyins found:', allBuyins, 'Error:', allBuyinsError);

      // Calculate total games played
      const uniqueGameIds = [...new Set(allBuyins?.map(b => b.game_id) || [])];
      const totalGamesCount = uniqueGameIds.length;

      // Get all settled games for buyin/payout calculations
      const { data: settledGames, error: gamesError } = await supabase
        .from('games')
        .select('id, title, status')
        .eq('status', 'settled');

      console.log('Settled games found:', settledGames, 'Error:', gamesError);

      if (!settledGames || settledGames.length === 0) {
        console.log('No settled games found');
        setTotalBuyins(0);
        setNetAmount(0);
        setTotalGames(totalGamesCount);
        setWins(0);
        setLosses(0);
        return;
      }

      const settledGameIds = settledGames.map(g => g.id);
      console.log('Settled game IDs:', settledGameIds);

      // Get all buyins for this user from settled games only
      const { data: buyins, error: buyinsError } = await supabase
        .from('buyins')
        .select('amount, player_id, game_id')
        .in('player_id', playerIds)
        .in('game_id', settledGameIds);

      console.log('Buyins found:', buyins, 'Error:', buyinsError);

      // Get all payouts for this user (payouts only exist for settled games)
      const { data: payouts, error: payoutsError } = await supabase
        .from('payouts')
        .select('net_amount, player_id')
        .in('player_id', playerIds);

      console.log('Payouts found:', payouts, 'Error:', payoutsError);

      const totalBuyinAmount = (buyins || []).reduce((sum, buyin) => sum + buyin.amount, 0);
      const totalNetAmount = (payouts || []).reduce((sum, payout) => sum + payout.net_amount, 0);
      
      // Calculate wins and losses
      const winsCount = (payouts || []).filter(p => p.net_amount > 0).length;
      const lossesCount = (payouts || []).filter(p => p.net_amount <= 0).length;

      console.log('Final calculations:', { 
        totalBuyinAmount, 
        totalNetAmount,
        totalGamesCount,
        winsCount,
        lossesCount,
        buyinCount: buyins?.length || 0,
        payoutCount: payouts?.length || 0
      });

      setTotalBuyins(totalBuyinAmount);
      setNetAmount(totalNetAmount);
      setTotalGames(totalGamesCount);
      setWins(winsCount);
      setLosses(lossesCount);
    } catch (error) {
      console.error('Error calculating user stats:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onLogout();
  };

  if (loading || !sessionReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {!sessionReady ? "Establishing secure connection..." : "Loading your poker groups..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">SP</span>
            </div>
            <h1 className="text-xl font-bold text-foreground">SplitPot</h1>
          </div>
          <UserProfileDropdown user={user} onLogout={handleLogout} />
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">Your Poker Groups</h2>
          <p className="text-muted-foreground">Manage your poker games and track winnings across different groups.</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Buy-in</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{totalBuyins.toLocaleString()}</div>
              <div className={`text-xs ${netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {netAmount >= 0 ? '+' : ''}₹{netAmount.toFixed(2)} overall
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Games</CardTitle>
              <GamepadIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalGames}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Wins & Losses</CardTitle>
              <Trophy className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{wins}</div>
              <div className="text-xs text-red-600">{losses} losses</div>
            </CardContent>
          </Card>
        </div>

        {/* Groups Section */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            Groups ({groups.length})
          </h3>
          <Button onClick={() => setShowCreateGroup(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Group
          </Button>
        </div>

        {groups.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No groups yet</h3>
              <p className="text-muted-foreground mb-4">Create your first poker group to get started</p>
              <Button onClick={() => setShowCreateGroup(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Group
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group) => (
              <GroupCard key={group.id} group={group} user={user} onUpdate={fetchGroups} />
            ))}
          </div>
        )}

        <CreateGroupDialog
          open={showCreateGroup}
          onOpenChange={setShowCreateGroup}
          onGroupCreated={fetchGroups}
        />
      </div>
      
      {/* Only show footer if not in PWA standalone mode */}
      {!isStandalone && <Footer />}
      
      {/* Auth Debug Panel - only in development */}
      {import.meta.env.DEV && (
        <div className="fixed bottom-4 left-4 z-50">
          <div className="text-xs text-muted-foreground bg-background/90 backdrop-blur-sm p-2 rounded border">
            Dashboard User: {user?.email} ({user?.id?.substring(0, 8)}...)
          </div>
        </div>
      )}
    </div>
  );
};
